/**
 * Futures slug parsing — grouping a gameless Polymarket future leg into the
 * container (tournament / award / season family) it belongs to.
 *
 * WHY THIS EXISTS: hydraedge stamps `marketType: 'future'` on dateless
 * Polymarket legs but forwards NO container identity — `eventTicker` on a
 * future frame is the market's own `conditionId` (ConsumerHub.marketFrame),
 * so every leg looks like its own event. The Gamma event slug that WOULD name
 * the tournament is known to the edge (`m.events[].slug`) and dropped on the
 * wire. Until a container field exists, the slug is the only grouping signal
 * we have, so this module derives one from it.
 *
 * Two shapes cover the live inventory (verified against he1/he2 REST
 * snapshots, 2026-08-21 — 2017 GOLF + 980 CFB legs):
 *
 *   A. TOURNAMENT — `[tour-]YYYY-<tournament>-<kind>-<subject>`
 *      `2026-bmw-championship-winner-ludvig-aberg-win`
 *      `lpga-2026-cpkc-womens-open-third-round-leader-carlota-ciganda`
 *      The kind token splits the container from the subject, and one
 *      tournament card holds every kind (winner + top5/10/20 + round leaders).
 *
 *   B. PROSE — `will-<subject>-<predicate>[-<numeric id>]`
 *      `will-alabama-have-more-than-6pt5-wins-during-the-2026-cfb-season-2026…`
 *      `will-byu-cougars-win-the-2026-big-12-championship-20260731203851985`
 *      The first ANCHOR verb splits subject from predicate; the predicate with
 *      its numbers normalized to `#` is the container, so every team's win
 *      total boards together and the varying line rides the leg.
 *
 * Anything else returns null and the caller boards it as ungrouped rather than
 * guessing — a wrong container silently merges unrelated markets, which on a
 * click-to-trade surface is worse than an honest "Ungrouped" pile.
 *
 * Pure + venue-agnostic: no store, no clock, no sport table beyond the golf
 * tour prefixes. Adding a tenant (tennis championships, World Cup winner) is
 * data, not code — shape B already parses them (verified on the live ATP/NBA
 * future populations).
 */

/** Round/finish kinds that split a tournament slug. Longest first so
 *  `-top20-` can never be claimed by a `-top2-` style prefix if one appears. */
const TOURNAMENT_KINDS: ReadonlyArray<readonly [token: string, label: string]> = [
  ['first-round-leader', 'R1 Leader'],
  ['second-round-leader', 'R2 Leader'],
  ['third-round-leader', 'R3 Leader'],
  ['fourth-round-leader', 'R4 Leader'],
  ['make-the-cut', 'Make the Cut'],
  ['winner', 'Winner'],
  ['top20', 'Top 20'],
  ['top10', 'Top 10'],
  ['top5', 'Top 5'],
];

/** Tour slugs that can lead a tournament slug. Mirrors hydraedge's
 *  `GOLF.polySportNames` (src/sport/golf.ts, 8/11-pinned). A slug that opens
 *  with the YEAR instead is the PGA Tour — Poly leaves the flagship tour
 *  unprefixed (`2026-bmw-championship-…`). */
const TOUR_PREFIXES: Record<string, string> = {
  pga: 'PGA', lpga: 'LPGA', liv: 'LIV', dpwt: 'DPWT', kft: 'KFT',
};

/** Verbs that separate the subject from the claim in a `will-…` slug. The
 *  FIRST one wins: `will-alabama-have-more-than-…` splits at `have`, leaving
 *  `alabama` as the subject even though `wins` appears later in the tail. */
const ANCHOR_VERBS = new Set([
  'win', 'wins', 'won', 'make', 'makes', 'be', 'have', 'has', 'finish',
  'finishes', 'lead', 'leads', 'score', 'scores', 'reach', 'reaches',
  'qualify', 'qualifies', 'miss', 'misses', 'get', 'gets', 'receive',
  'receives', 'record', 'records', 'rank', 'ranks',
]);

/** Leading words stripped when a container title is derived from prose — the
 *  anchor verb and the preposition/article that follows it carry no identity
 *  ("win the 2026 ACC Championship" → "2026 ACC Championship"). */
const TITLE_LEAD_NOISE = new Set([
  ...ANCHOR_VERBS,
  'the', 'a', 'an', 'of', 'in', 'for', 'at', 'to', 'on', 'during',
  'following', 'as', 'with', 'their', 'its', 'more', 'than', 'an',
]);

export interface ParsedFutureSlug {
  /** Stable grouping key within a sport. Never displayed raw — the board's
   *  title is derived from member titles (see `containerTitle`). */
  containerKey: string;
  /** Slug of the thing being bet on: player, team, coach. */
  subjectSlug: string;
  /** Sub-market label inside the container: 'Winner' / 'Top 5' for shape A,
   *  the normalized-out numbers ('6.5') for shape B, null when the container
   *  IS the whole market. */
  kindLabel: string | null;
  /** Tour/competition tenant inside the sport ('PGA', 'LPGA', …) or null. */
  tour: string | null;
}

/** Numbers Poly encodes in slugs: `6pt5` (6.5), `2026`, `10`. */
const NUMERIC = /^\d+(?:pt\d+)?$/;

/**
 * Derive a container + subject from a Polymarket future ticker (`PM-<slug>`).
 * Returns null when neither shape matches — caller boards it as ungrouped.
 */
export function parseFutureSlug(ticker: string): ParsedFutureSlug | null {
  if (!ticker.startsWith('PM-')) return null;
  const slug = ticker.slice(3);

  // ── Shape A: tournament ────────────────────────────────────────────
  for (const [token, label] of TOURNAMENT_KINDS) {
    const at = slug.indexOf(`-${token}-`);
    if (at <= 0) continue;
    const container = slug.slice(0, at);
    const subject = slug.slice(at + token.length + 2);
    const lead = container.split('-')[0];
    return {
      containerKey: container,
      // `-win` is Poly's suffix on outright-winner legs
      // (`…-winner-ludvig-aberg-win`); it names no part of the player.
      subjectSlug: subject.replace(/-win$/, ''),
      kindLabel: label,
      tour: TOUR_PREFIXES[lead] ?? (/^\d{4}$/.test(lead) ? 'PGA' : null),
    };
  }

  // ── Shape B: prose ─────────────────────────────────────────────────
  if (!slug.startsWith('will-')) return null;
  let tokens = slug.slice(5).split('-');
  // Poly appends a capture-time id to many slugs (`…-20260731203851985`) and a
  // short disambiguator to others (`…-tour-championship-998`). Both are
  // per-leg noise that would shatter the container. Only 3+ digits, so a real
  // trailing token like a year inside prose is left alone by shape, not luck:
  // a year only ever appears mid-slug in this inventory.
  if (tokens.length > 1 && /^\d{3,}$/.test(tokens[tokens.length - 1])) {
    tokens = tokens.slice(0, -1);
  }
  const anchor = tokens.findIndex((t) => ANCHOR_VERBS.has(t));
  // anchor at 0 = no subject before the verb (`will-be-…`) — not a leg shape.
  if (anchor <= 0) return null;

  const numbers: string[] = [];
  const tail = tokens.slice(anchor).map((t) => {
    if (!NUMERIC.test(t)) return t;
    numbers.push(t.replace('pt', '.'));
    // Every number normalizes, including one that is part of a NAME
    // (`big-12` → `big-#`). That is deliberate: the alternative is failing to
    // group win totals, which is the far more common shape. The consequence is
    // narrow — only two numerically-named families in one sport could collide
    // — and the board's TITLE comes from the legs' prose, so a Big 12 card
    // still reads "2026 Big 12 Championship".
    return '#';
  });
  return {
    containerKey: tail.join('-'),
    // Poly writes team subjects with the article ("will-the-new-york-yankees-
    // win-…"). "The New York Yankees" reads wrong in a leg column next to
    // "Alabama"; the article carries no identity.
    subjectSlug: tokens.slice(0, anchor).join('-').replace(/^the-/, ''),
    // The numbers that were normalized OUT of the container are what
    // distinguishes one leg from another inside it (Alabama o6.5 vs BYU o10.5).
    // Years are shared by every leg, so they'd be noise on the row — drop a
    // leading 4-digit year and keep the rest.
    kindLabel: numbers.filter((n) => !/^\d{4}$/.test(n)).join(' / ') || null,
    tour: null,
  };
}

/** Slug → display text: `ludvig-aberg` → `Ludvig Aberg`. Acronyms Poly writes
 *  lowercase stay uppercase so `byu` reads BYU, not Byu. */
const ACRONYMS = new Set([
  'byu', 'tcu', 'lsu', 'ucf', 'usc', 'ucla', 'smu', 'unlv', 'utep', 'uab',
  'fiu', 'fau', 'utsa', 'nfl', 'nba', 'mlb', 'nhl', 'cfb', 'cfp', 'ncaa',
  'atp', 'wta', 'ufc', 'pga', 'lpga', 'liv', 'kft', 'dpwt', 'acc', 'sec',
  'qb', 'rb', 'wr', 'te', 'dst', 'd1', 'hc', 'mvp',
]);

/** A 3–4 letter token with no vowel is an initialism Poly wrote lowercase
 *  (`bmw`, `cpkc`, `byu`). Two letters is too short to be sure — `st` in
 *  `st-jude` would come back as ST. */
const isInitialism = (w: string) => w.length >= 3 && w.length <= 4 && !/[aeiou]/.test(w);

export function humanizeSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w) || isInitialism(w)
      ? w.toUpperCase()
      : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * Board title from the legs' own prose titles — the properly-cased, accented
 * text Poly wrote, rather than a title-cased slug.
 *
 * Method: the longest word-wise COMMON SUFFIX across member titles is the part
 * that describes the container rather than the subject ("Will {X} win the 2026
 * BMW Championship?" ∩ "Will {Y} finish in the top 5 of the 2026 BMW
 * Championship?" → "the 2026 BMW Championship?"), then the leading verb /
 * preposition noise is trimmed. Falls back to the humanized container key when
 * the titles share too little to name anything.
 */
export function containerTitle(titles: readonly string[], containerKey: string): string {
  const fallback = humanizeSlug(containerKey.replace(/#/g, '').replace(/--+/g, '-'));
  if (titles.length === 0) return fallback;

  const wordLists = titles.map((t) => t.trim().split(/\s+/));
  // A single-member board (or a board whose legs happen to share a whole
  // title) teaches nothing: the "common suffix" is the entire question,
  // subject included — "Will Abraham Ancer finish in the Top 10 at the 2026
  // Indianapolis Individual" is a leg, not a board name. Fall back to the
  // container key, which is exactly the tournament.
  if (wordLists.length < 2) return fallback;
  let suffix = wordLists[0];
  for (const words of wordLists.slice(1)) {
    let n = 0;
    while (
      n < suffix.length && n < words.length &&
      suffix[suffix.length - 1 - n].toLowerCase() === words[words.length - 1 - n].toLowerCase()
    ) n++;
    suffix = suffix.slice(suffix.length - n);
    if (suffix.length === 0) break;
  }

  const cleaned = [...suffix];
  // Trailing "?" belongs to the question form, not the name.
  if (cleaned.length) cleaned[cleaned.length - 1] = cleaned[cleaned.length - 1].replace(/[?.]+$/, '');
  while (cleaned.length && TITLE_LEAD_NOISE.has(cleaned[0].toLowerCase())) cleaned.shift();
  while (cleaned.length && !cleaned[cleaned.length - 1]) cleaned.pop();

  // One bare word is more likely a shared noun ("season") than a name, and a
  // suffix as long as the shortest member title means nothing was shared away
  // — both cases fall back to the container key.
  const shortest = Math.min(...wordLists.map((w) => w.length));
  if (cleaned.length < 2 || suffix.length >= shortest) return fallback;
  return cleaned.join(' ').replace(/^./, (c) => c.toUpperCase());
}
